import auth0 from 'auth0-js'
import axios from 'axios'

class Auth0Service {
  auth = new auth0.WebAuth({
    domain: process.env.REACT_APP_DOMAIN,
    clientID: process.env.REACT_APP_CLIENT_ID,
    redirectUri: process.env.REACT_APP_REDIRECT_URI,
    audience: process.env.REACT_APP_AUDIENCE,
    scope: process.env.REACT_APP_SCOPE,
    responseType: process.env.REACT_APP_RESPONSE_TYPE,
  })

  /**
  * @author Leonel Contreras
  * @description delete all localStorage info
  * @memberof AuthService
  */
  logout = async () => {
    await this.auth.logout({
      returnTo: process.env.REACT_APP_REDIRECT_URI
    })
  }

  authorize = async () => {
    this.auth.authorize()
  }

  /**
   * @author Leonel Contreras
   * @description validate a token
   * @param {Object} token
   * @param {Object} nonce
   * @return {Object}
   * @memberof AuthService
   */
  validateToken = async (token, nonce) => {
    let promise = new Promise((resolve, reject) => {
      this.auth.validateToken(token, nonce, (err, response) => {
        err ? reject(err) : resolve(response)
      })
    })

    let response = await promise
    return response
  }

  /**
   * @author Leonel Contreras
   * @description get token's info
   * @param {String} hash
   * @return {Object}
   * @memberof AuthService
   */
  getTokenData = async (hash) => {
    let promise = new Promise((resolve, reject) => {
      this.auth.parseHash({ hash }, (err, response) => {
        err ? reject(err) : resolve(response)
      })
    })

    let request = await promise
    return request
  }

  /**
   * @author Leonel Contreras
   * @description get user's info
   * @param {String} token
   * @return {Object}
   * @memberof AuthService
   */
  getUserProfile = async (token) => {
    let promise = new Promise((resolve, reject) => {
      this.auth.client.userInfo(token, (err, response) => {
        err ? reject(err) : resolve(response)
      })
    })

    let response = await promise
    return response
  }

  /**
  * @author Alan Aguilar
  * @description Login an user in hibou.legal by Auth0 info
  * @date 11-05-2021
  * @param {String} email
  * @param {String} password
  * @return {Object}
  * @memberof AuthService
  */
  login = async (email, password) => {
    let promise = new Promise((resolve, reject) => {
      this.auth.login({
        realm: process.env.REACT_APP_LOGIN_REAML,
        username: email,
        password
      }, (err, result) => {
        err ? reject(err) : resolve(result)
      })
    })

    let response = await promise
    return response
  }

  // [11-05-2021] PRIMERA VERSION FUNCIONAL DE LOGIN
  // login = async (email, password) => {
  //   let promise = new Promise((resolve, reject) => {
  //     this.auth.client.login({
  //       realm: process.env.REACT_APP_LOGIN_REAML,
  //       username: email,
  //       password
  //     }, (err, result) => {
  //       if (err) {
  //         reject(err)
  //       } else {
  //         localStorage.setItem('token', JSON.stringify(result))
  //         resolve(result)
  //       }
  //     })
  //   })

  //   let response = await promise
  //   return response
  // }

  /**
  * @author Alan Aguilar
  * @description Send a reset password email to an user by Auth0
  * @date 16-11-2020
  * @param {String} resetEmail
  * @return {Object}
  * @memberof AuthService
  */
  resetPassword = async (resetEmail) => {
    let promise = new Promise((resolve, reject) => {
      this.auth.changePassword({
        connection: process.env.REACT_APP_LOGIN_REAML,
        email: resetEmail
      }, (err, result) => {
        if (err) {
          reject(err)
        } else {
          resolve(result)
        }
      })
    })

    let response = await promise
    return response
  }

  /**
  * @author Alan Aguilar
  * @description Create a new user in Auth0 and then send verification email
  * @date 16-11-2020
  * @param {String} newEmail
  * @param {String} newPassword
  * @param {String} username
  * @return {Object}
  * @memberof AuthService
  */
  signUp = async (newEmail, newPassword, username) => {
    let promise = new Promise((resolve, reject) => {
      this.auth.signup({
        connection: process.env.REACT_APP_LOGIN_REAML,
        email: newEmail,
        password: newPassword,
        username
      }, (err, result) => {
        if (err) {
          reject(err)
        } else {
          localStorage.setItem('token', JSON.stringify(result))
          resolve(result)
        }
      })
    })

    let response = await promise
    return response
  }

  /**
  * @author Alan Aguilar
  * @description Refresh a sesion token App from Auth0
  * @updatedDate 11-05-2021
  * @return {Object}
  * @memberof AuthService
  */
  refreshToken = async () => {
    let promise = new Promise((resolve, reject) => {
      this.auth.checkSession({}, (err, result) => {
        if (err) {
          reject(err)
        } else {
          resolve(result)
        }
      })
    })

    let response = await promise
    return response
  }

  /**
  * @author Alan Aguilar
  * @description Check if the current token is valid and try to renewal it
  * @date 18-11-2020
  * @return {Object}
  * @memberof AuthService
  */
  checkToken = async () => {
    let promise = new Promise((resolve, reject) => {
      this.auth.checkSession({
        audience: process.env.REACT_APP_AUDIENCE,
        scope: process.env.REACT_APP_SCOPE
      }, (err, result) => {
        if (err) {
          reject(err)
        } else {
          resolve(result)
        }
      })
    })

    let response = await promise
    return response
  }

  /**
  * @author Alan Aguilar
  * @description Send email of verification of a user in A3Cloud
  * @date 14-12-2020
  * @param {String} auth0Id
  * @param {String} token
  * @return {Object}
  * @memberof AuthService
  */
  sendEmailverification = async (auth0Id, token) => {
    let response = await axios.post(`${process.env.REACT_APP_MIFIRMA_API}/email-verification`,
      { auth0Id }, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    return response
  }

  /**
  * @author Alan Aguilar
  * @description Change the user password in Mifirma and Auth0
  * @date 15-12-2020
  * @param {String} newPassword
  * @param {String} token
  * @return {Object}
  * @memberof AuthService
  */
  changePasswordRequest = async (newPassword, token, email, verifyActualPassword, actualPassword) => {
    let response = await axios.post(`${process.env.REACT_APP_MIFIRMA_API}/user-change-password`,
      { newPassword, email, verifyActualPassword, actualPassword }, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    return response
  }

  /**
  * @author Alan Aguilar
  * @description Block an user in hibou.legal and Auth0
  * @date 24-12-2020
  * @param {String} email
  * @param {String} token
  * @return {Object}
  * @memberof AuthService
  */
  blockAnUser = async (email, token) => {
    let response = await axios.put(`${process.env.REACT_APP_MIFIRMA_API}/user-block`,
      { email }, {
      headers: {
        'Authorization': `Bearer ${token.access_token}`
      }
    })
    return response
  }

  /**
  * @author Brayan Salas
  * @description Change the user password in Govo and Auth0
  * @date 25-04-2024
  * @param {String} newPassword
  * @param {String} token
  * @param {String} origin
  * @return {Object}
  * @memberof AuthService
  */
  forgotPassword =  async (newPassword, token, origin) => {
    let response = await axios.post(`${process.env.REACT_APP_MIFIRMA_API}/forgot-password`,
    { newPassword, origin }, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    return response
  }
}

export { Auth0Service as default }